import React, { FC } from 'react';
import { HiChevronLeft, HiChevronRight, HiSelector } from 'react-icons/hi';
import classNames from 'classnames';
import AppButton from '@/components/atoms/AppButton';
import { Text } from '@/components/atoms/AppText';
import { tid } from '@/utils/testId';
import { ORGANISM_TEST_IDS } from '@/constant/testIds/oganism.testIds';

export interface PaginationProps {
  /** Current active page (1-based) */
  currentPage?: number;
  /** Total number of items from the API */
  totalItem?: number;
  /**
   * Explicitly override computed page count.
   * If omitted, derived as Math.ceil(totalItem / itemPerpage).
   */
  totalPages?: number;
  /** Rows-per-page options shown in the select */
  rowsPerPageOptions?: number[];
  /** Currently selected rows per page */
  itemPerpage?: number;
  onPreviousClick: (currentPage: number) => void;
  onNextClick: (currentPage: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  /**
   * When true renders as a fixed bottom-right pill (default).
   * When false renders inline with justify-end.
   */
  floating?: boolean;
  className?: string;
}

const Pagination: FC<PaginationProps> = ({
  currentPage = 1,
  totalItem = 0,
  totalPages: totalPagesProp,
  rowsPerPageOptions = [10, 20, 50],
  itemPerpage = 10,
  onRowsPerPageChange,
  onPreviousClick,
  onNextClick,
  floating = true,
  className,
}) => {
  // Derive totalPages from totalItem ÷ itemPerpage unless explicitly provided
  const totalPages =
    totalPagesProp ??
    (itemPerpage > 0 ? Math.ceil(totalItem / itemPerpage) : 1);

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;
  const { NEXT_BTN, PREV_BTN, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTION } = ORGANISM_TEST_IDS.PAGINATION_TID;

  return (
    <div
      className={classNames(
        floating ? 'fixed bottom-2 right-5 z-50' : 'flex justify-end',
        className,
      )}
    >
      <div
        className={classNames(
          'flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2',
          'dark:border-slate-700 dark:bg-slate-900',
          floating ? 'shadow-xl backdrop-blur-md' : 'shadow-sm',
        )}
      >
        {/* Total results */}
        {totalItem > 0 && (
          <div className="hidden sm:flex items-center gap-2 pr-3 border-r border-slate-200 dark:border-slate-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <Text as="span" size="base" weight="semibold" color="soft">
              {totalItem.toLocaleString()}{' '}
              <Text as="span" size="sm" weight="semibold" color="info">
                Results
              </Text>
            </Text>
          </div>
        )}

        {/* Rows-per-page selector */}
        <div className="relative flex items-center">
          <select
            value={itemPerpage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            data-testid={tid(ROWS_PER_PAGE)}
            className="cursor-pointer appearance-none rounded-lg bg-slate-100 px-3 py-1.5 pr-7 text-xs font-semibold text-slate-800 outline-none dark:bg-slate-800 dark:text-slate-100"
          >
            {rowsPerPageOptions.map((opt) => (
              <option key={opt} value={opt} className="dark:bg-slate-900" data-testid={tid(`${ROWS_PER_PAGE_OPTION}-${opt}`)}>
                {opt}
              </option>
            ))}
          </select>
          <HiSelector
            className="pointer-events-none absolute right-2 text-slate-500 dark:text-slate-300"
            size={14}
          />
        </div>

        {/* Previous */}
        <AppButton
          variant={isFirst ? 'neutral' : 'primary'}
          shape="square"
          size="sm"
          onClick={() => !isFirst && onPreviousClick(currentPage)}
          disabled={isFirst}
          icon={<HiChevronLeft size={18} />}
          className="!rounded-full"
          aria-label="Previous page"
          data-testid={tid(PREV_BTN)}
        />

        {/* Page indicator */}
        <div className="flex min-w-[80px] items-center justify-center rounded-xl bg-slate-100 px-3 py-1 dark:bg-slate-800">
          <Text as="span" size="base" weight="bold" color="soft">
            {currentPage}
          </Text>
          <Text as="span" size="base" color="subtle" className="mx-1">
            /
          </Text>
          <Text as="span" size="base" weight="bold" color="soft">
            {totalPages}
          </Text>
        </div>

        {/* Next */}
        <AppButton
          variant={isLast ? 'neutral' : 'primary'}
          shape="square"
          size="sm"
          onClick={() => !isLast && onNextClick(currentPage)}
          disabled={isLast}
          icon={<HiChevronRight size={18} />}
          className="!rounded-full"
          aria-label="Next page"
          data-testid={tid(NEXT_BTN)}
        />
      </div>
    </div>
  );
};

export default Pagination;
